(function () {

    'use strict';

    if (typeof window === 'undefined') {

        module.exports = exportFn(
            require('../modules/data/channels.js'),
            require('../enums/colors.js'),
            require('../enums/metrics.js'),
            require('../strings/strings.js'),
            require('../modules/utils/metric.js'),
            require('../modules/utils/keywords.js'),
            undefined, // views are not used server-side
            require('lodash')
        );

    } else {
        RivalIQ.metrics.definitions.youtube = exportFn(
            RivalIQ.data.channels,
            RivalIQ.enums.colors,
            RivalIQ.enums,
            RivalIQ.strings,
            RivalIQ.utils.metric,
            RivalIQ.utils.keywords,
            RivalIQ.views.metric,
            window._
        );
    }

    function exportFn(channels, colors, metricEnums, strings, utils, keywordsUtils, views, _) {
        function postOrPostsString(a, b) {
            return function (val, metric) {
                if (utils.useDetailsDescription(metric)) {
                    return a;
                }
                return b;
            };
        }

        function withDescriptionNote(description, note) {
            if (_.isFunction(description)) {
                return function () {
                    const base = description.apply(null, arguments);
                    return base ? `${base} ${note}` : note;
                };
            }
            return description ? `${description} ${note}` : note;
        }

        function withPublicViewCountChangeNote(description) {
            return withDescriptionNote(description, strings.youtube_public_view_count_change_description);
        }

        function withViewCountDenominatorChangeNote(description) {
            return withDescriptionNote(
                withPublicViewCountChangeNote(description),
                strings.youtube_view_count_denominator_change_description
            );
        }

        const channel = channels.get('youtube');

        return {
            className: channel.get('channel'),
            format: channel.get('name'),
            name: channel.get('name'),
            color: colors.youTubeHighContrast,
            engagement_total: {
                description: postOrPostsString(strings.youtube_post_engagement_total_description, strings.youtube_posts_engagement_total_description)
            },
            engagement_total_per_post: {
                description: strings.youtube_engagement_total_per_post_description
            },
            engagement_rate: {
                description: postOrPostsString(strings.youtube_post_engagement_rate_description, strings.youtube_posts_engagement_rate_description)
            },
            avg_engagement_rate_per_post: {
                description: strings.youtube_average_post_engagement_rate_description
            },
            audience: {
                name: 'Subscribers',
                description: strings.youtube_subscribers_description,
                metricType: metricEnums.metricType.TOTAL,
                category: 'post_analysis',
                collection: metricEnums.collection.AUDIENCE,
                noHistoricData: true
            },
            presence_reach: {
                name: 'Subscribers',
                description: strings.youtube_subscribers_description,
                noHistoricData: true
            },
            potential_reach: {
                description: strings.youtube_potential_reach_description
            },
            avg_count_per_day: {
                description: strings.youtube_average_daily_posts_description
            },
            avg_count_per_week: {
                description: strings.youtube_avg_count_per_week_description
            },
            conversation: {
                name: 'Comments, Lifetime',
                description: strings.youtube_comments_description,
                collection: metricEnums.collection.ENGAGEMENT_DETAILS
            },
            avg_engagement_total_per_day: {
                description: strings.youtube_average_daily_posts_engagement_total_description
            },
            engagement_type: {
                // This defines the order these will appear in bar charts.
                dimensionValues: _.constant(['applause', 'conversation']),

                value: {
                    applause: {
                        format: utils.formatDimensionFn('Likes'),
                        className: 'x-youtube',
                        engagement_total: {
                            name: 'Likes',
                            description: postOrPostsString(strings.youtube_post_likes_description, strings.youtube_posts_likes_description)
                        },
                        avg_engagement_total_per_day: {
                            name: 'Likes / Day',
                            description: strings.youtube_average_daily_posts_likes_description
                        }
                    },
                    conversation: {
                        format: utils.formatDimensionFn('Comments'),
                        className: 'x-youtube-tertiary',
                        engagement_total: {
                            name: 'Comments',
                            description: postOrPostsString(strings.youtube_post_comments_description, strings.youtube_posts_comments_description)
                        },
                        avg_engagement_total_per_day: {
                            name: 'Comments / Day',
                            description: strings.youtube_average_daily_posts_comments_description
                        }
                    }
                }
            },
            engagement_total_per_view: {
                name: 'Engagement Rate by View',
                description: withViewCountDenominatorChangeNote(function (val, metric) {
                    if (_.includes(metric.dimensions, 'post')) {
                        return strings.youtube_engagement_total_per_view_post_description;
                    } else {
                        return strings.youtube_engagement_total_per_view_description;
                    }
                }),
                collection: metricEnums.collection.ENGAGEMENT,
                summable: false,
                type: metricEnums.dataType.ABSOLUTE_PERCENT,
                metricValueType: metricEnums.metricValueType.AVERAGE,
                cellView: function (data, metric) {
                    return (data[0] !== '__SUMMARY_ROW__' && metric.isNotRelative(data) ? views.Bar : undefined);
                }
            },
            engagement_rate_by_view: {
                name: 'Engagement Rate by View',
                description: withViewCountDenominatorChangeNote(function (val, metric) {
                    if (_.includes(metric.dimensions, 'post')) {
                        return strings.youtube_engagement_total_per_view_post_description;
                    } else {
                        return strings.youtube_engagement_total_per_view_description;
                    }
                })
            },
            page_views: {
                name: 'Views, Lifetime',
                metricType: metricEnums.metricType.TOTAL,
                description: withPublicViewCountChangeNote(strings.youtube_views_description),
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS
            },
            videos: {
                name: 'Videos',
                metricType: metricEnums.metricType.TOTAL,
                description: strings.youtube_videos_description,
                collection: metricEnums.collection.ACTIVITY
            },
            count: {
                name: 'Posts',
                description: strings.youtube_posts_description
            },
            views: {
                name: 'Views',
                category: 'post_analysis',
                description: withPublicViewCountChangeNote(postOrPostsString(strings.youtube_post_views_description, strings.youtube_posts_views_description)),
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS
            },
            avg_views_per_day: {
                name: 'Views / Day',
                metricType: metricEnums.metricType.VOLUME,
                description: withPublicViewCountChangeNote(strings.youtube_average_daily_posts_views_description),
                collection: metricEnums.collection.VIDEO_VIEWS_DETAILS
            },
            estimated_impressions: {
                description: withPublicViewCountChangeNote(postOrPostsString(
                    strings.post_estimated_impressions_description,
                    strings.estimated_impressions_description
                ))
            },
            engagement_rate_by_estimated_impression: {
                description: withViewCountDenominatorChangeNote(postOrPostsString(
                    strings.post_engagement_rate_by_estimated_impression_description,
                    strings.engagement_rate_by_estimated_impression_description
                ))
            },
            overview: {
                name: 'Description',
                description: strings.youtube_overview_description
            },
            title: {
                description: strings.youtube_title_description
            },
            title_size: {
                name: 'Title Size',
                description: strings.youtube_title_size_description
            },
            keywords: {
                description: strings.youtube_keywords_description,
                format: function (data) {
                    return keywordsUtils.formatQuotedKeywords(data[data.length - 1]);
                }
            }
        };
    }
})();
